import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/_components/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/_components/table";
import { useGetTransactionTelemetry } from "@/lib/api/hooks";
import { Loader2, BarChart3, Clock, Zap } from "lucide-react";

interface TelemetryData {
  agentName: string;
  inputTokens: number;
  outputTokens: number;
  timeTaken: number;
}

interface TelemetryDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: number;
}

const TelemetryDetailsDialog = ({
  isOpen,
  onClose,
  transactionId,
}: TelemetryDetailsDialogProps) => {
  const { data: telemetryData, isLoading, error } = useGetTransactionTelemetry(
    transactionId,
    {
      query: {
        enabled: isOpen && !!transactionId,
      },
    }
  );

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <BarChart3 className="h-5 w-5 text-primary" />
            AI Response Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2 text-primary" />
              <span className="text-muted-foreground">Loading telemetry data...</span>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-destructive">Failed to load telemetry data</p>
            </div>
          )}

          {telemetryData && telemetryData.data && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold text-foreground">Agent Name</TableHead>
                    <TableHead className="font-semibold text-foreground">
                      <div className="flex items-center gap-1">
                        <Zap className="h-4 w-4" />
                        Input Tokens
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      <div className="flex items-center gap-1">
                        <Zap className="h-4 w-4" />
                        Output Tokens
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Time Taken (ms)
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {telemetryData.data.map((item: TelemetryData, index: number) => (
                    <TableRow key={index} className="transition-colors hover:bg-muted/50">
                      <TableCell className="font-medium text-foreground">{item.agentName}</TableCell>
                      <TableCell className="text-foreground">{formatNumber(item.inputTokens)}</TableCell>
                      <TableCell className="text-foreground">{formatNumber(item.outputTokens)}</TableCell>
                      <TableCell className="text-foreground">{formatNumber(item.timeTaken)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {telemetryData && (!telemetryData.data || telemetryData.data.length === 0) && (
            <div className="text-center py-8">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No Telemetry Data</h3>
                <p className="text-muted-foreground">
                  No telemetry data is available for this transaction.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TelemetryDetailsDialog;